import { log } from 'log';

/**
 * Create script DOM element with provided params
 * @function
 * @param [params.src] {String} script src
 * @param [params.code] {String} script inner code
 * @param [params.attrs] {Object} script attributes
 * @returns {Node}
 * @example createScriptElement({ code: 'console.log("script")', attrs: { 'async': true } })
 */
export const createScriptElement = ({ src, code, attrs = {} }) => {
    const script = document.createElement('script');

    script.type = 'text/javascript';

    for (const property in attrs) {
        script.setAttribute(property, attrs[property]);
    }

    if (code) {
        script.appendChild(document.createTextNode(code));
    }

    if (src) {
        script.src = src;
    }

    return script;
};

/**
 * Appends script to the head and removes it after that;
 * @function
 * @param [params.src] {String} script src
 * @param [params.code] {String} script inner code
 * @param [params.attrs] {Object} script attributes
 * @example evalJavaScript({ code: 'console.log("script")', attrs: { 'async': true } })
 */
export const evalJavaScript = (options) => {
    const head = document.querySelector('head');
    const script = createScriptElement(options);

    head.append(script);
    script.remove();
};

/**
 * Finds all script elements within a provided HTML;
 * @function
 * @param [html] {String} HTML as a simple string
 * @example findScriptElements('<div><script>console.log("test")</script></div>')
 */
export const findScriptElements = (html) => {
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const scripts = doc.querySelectorAll('script');

        return [...scripts].map(({ textContent, attributes }) => ({
            code: textContent,
            attrs: Object.assign({},
                ...Array.from(attributes, ({ name, value }) => ({ [name]: value }))
            )
        }));

    } catch (e) {
        log('error', e);
    }

    return [];
};

/**
 * Runs all script found within a provided HTML;
 * @function
 * @param [html] {String} HTML as a simple string
 * @example findScriptElements('<div><script>console.log("test")</script></div>')
 */
export const evalAllScriptsInHtml = (html) => {
    const scripts = findScriptElements(html);

    scripts.forEach((script) => evalJavaScript(script));
};

/**
 * Checks whether provided string is a valid HTML
 * @function
 * @param [html] {String} HTML as a simple string
 * @returns {Boolean}
 * @example isHtml('<div><script>console.log("test")</script></div>')
 */
export const isHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
};

/**
 * Insert HTML and return a function that removes inserted elements from DOM.
 * @function
 * @param [params.selector] {String} selector of element where HTML will be inserted in
 * @param [params.html] {String} HTML to be inserted
 * @param [params.position] {String} position of the appended html (beforeend | afterbegin)
 * @param [params.tagName] {String} Tag name for comments
 * @param [params.wrapperComment] {String} Comment to the provided HTML
 */
export const insertTrackableHTML = ({ selector, html, position = 'beforeend', tagName, wrapperComment }) => {
    const elements = [];
    const el = document.querySelector(selector);
    const renderer = document.createElement('div');
    const beforeComment = document.createComment(wrapperComment ? ` ${wrapperComment} ` : ` Tag name is ${tagName} `);
    const afterComment = document.createComment(wrapperComment ? ` End ${wrapperComment} ` : ` End tag name is ${tagName} `);

    renderer.innerHTML = html;

    if (position === 'beforeend' || wrapperComment) {
        el.append(beforeComment);
    }

    Array.from(renderer.children).forEach((element) => {
        elements.push(element);
        el.insertAdjacentElement(position, element);
    });

    if (wrapperComment) {
        el.append(afterComment);
    }

    if (position === 'afterbegin' && !wrapperComment) {
        el.prepend(beforeComment);
    }

    evalAllScriptsInHtml(html);

    return () => {
        for (const element of elements) {
            element.remove();
        }
        beforeComment.remove();
        afterComment.remove();
    };
};

/**
 * @function
 * @param element {HTMLElement}
 * @returns boolean
 */
export const isMirrorModeElement = (element) => getComputedStyle(element).direction === 'rtl';

/**
 * @function
 * @param {HTMLElement} element
 * @param {Number} percentage from 0 to 1 where 1 is 100%
 * @returns {Boolean}
 */
export const isInViewportByPercentage = (element, percentage) => {
    if (!element) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    return !(
        Math.floor(100 - (rect.top >= 0 ? 0 : rect.top) / -rect.height * 100) < 100 * percentage ||
        Math.floor(100 - (rect.bottom - windowHeight) / rect.height * 100) < 100 * percentage
    );
};
